import Papa from "papaparse";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  Timestamp,
} from "firebase/firestore";
import { get, ref } from "firebase/database";
import { firestore, database } from "../firebaseConfig";

const PAGE_SIZE = 500;

export const SKIP_COLUMNS = new Set([
  "_local_status_update",
  "_optimistic_update",
  "_critical_update",
  "_prevent_refresh",
  "_client_update_timestamp",
]);

const PRIORITY_ORDER = [
  "id",
  "lead_email",
  "business_name",
  "lead_firstname",
  "lead_lastname",
  "lead_telephone",
  "lead_website",
  "lead_status",
  "lead_contacted",
  "lead_since",
  "import_name",
  "imported_at",
  "created_at",
  "updated_at",
];

export const parseDecisionMakers = (raw) => {
  if (!raw) return [];
  try {
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch (_) { /* malformed → ignore */ }
  return [];
};

export const pickPrimaryDecisionMaker = (dms) => {
  if (!Array.isArray(dms) || dms.length === 0) return null;
  const contacted = dms.find((dm) => dm && dm.contacted && dm.name);
  if (contacted) return contacted;
  return dms.find((dm) => dm && dm.name) || null;
};

export const splitDecisionMakerName = (rawName) => {
  if (!rawName || typeof rawName !== "string") return { firstName: "", lastName: "" };
  const parts = rawName.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  const [firstName, ...rest] = parts;
  return { firstName, lastName: rest.join(" ") };
};

export const applyPrimaryDecisionMakerToName = (row) => {
  const primary = pickPrimaryDecisionMaker(parseDecisionMakers(row.lead_decision_makers));
  if (!primary) return false;
  const { firstName, lastName } = splitDecisionMakerName(primary.name);
  if (!firstName && !lastName) return false;
  row.lead_firstname = firstName;
  row.lead_lastname = lastName;
  return true;
};

export const formatValue = (value) => {
  if (value === null || value === undefined) return "";
  if (value instanceof Timestamp) return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) {
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  if (typeof value === "object") {
    if (typeof value.toDate === "function") {
      try { return value.toDate().toISOString(); } catch { /* fall through */ }
    }
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  if (typeof value === "boolean") return value ? "true" : "false";
  return String(value);
};

export const orderColumns = (keys) => {
  const present = new Set(keys);
  const ordered = [];
  PRIORITY_ORDER.forEach((k) => {
    if (present.has(k)) {
      ordered.push(k);
      present.delete(k);
    }
  });
  return [...ordered, ...Array.from(present).sort((a, b) => a.localeCompare(b))];
};

export const todayStamp = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const uniqueStrings = (values) => {
  if (!Array.isArray(values)) return [];
  const out = [];
  const seen = new Set();
  values.forEach((raw) => {
    if (typeof raw !== "string") return;
    const value = raw.trim();
    if (!value || seen.has(value)) return;
    seen.add(value);
    out.push(value);
  });
  return out;
};

export const normalizeImportNames = (importNames) => uniqueStrings(importNames);

export const resolveDownloadScope = (mode, selectedNames) => {
  if (mode !== "lists") return { importNames: null };
  return { importNames: normalizeImportNames(selectedNames) };
};

export const canStartDownload = (mode, selectedNames) => {
  if (mode !== "lists") return true;
  return resolveDownloadScope(mode, selectedNames).importNames.length > 0;
};

export const slugifyListName = (name) =>
  String(name || "")
    .trim()
    .replace(/[^a-zA-Z0-9._-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40) || "list";

export const downloadFilename = (mode, selectedNames) => {
  const stamp = todayStamp();
  if (mode !== "lists") return `contacts_all_lists_${stamp}.csv`;
  const names = normalizeImportNames(selectedNames);
  if (names.length === 1) return `contacts_${slugifyListName(names[0])}_${stamp}.csv`;
  return `contacts_${names.length}_lists_${stamp}.csv`;
};

export const triggerCsvDownload = (rows, columns, filename) => {
  const csv = Papa.unparse(
    { fields: columns, data: rows.map((row) => columns.map((col) => formatValue(row[col]))) },
    { quotes: true, newline: "\r\n" }
  );
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

export const loadCustomFieldKeys = async (userId) => {
  if (!userId) return [];
  try {
    const snap = await get(ref(database, `World/${userId}/settings/customFields`));
    if (!snap.exists()) return [];
    return Object.values(snap.val() || {})
      .map((field) => field && field.key)
      .filter((key) => typeof key === "string" && key.trim());
  } catch (_) {
    return [];
  }
};

const appendContactDoc = (docSnap, rows, keys, includeRow) => {
  const data = docSnap.data() || {};
  if (includeRow && !includeRow(data)) return;
  const row = { id: docSnap.id };
  Object.entries(data).forEach(([k, v]) => {
    if (SKIP_COLUMNS.has(k)) return;
    row[k] = v;
    keys.add(k);
  });
  if (applyPrimaryDecisionMakerToName(row)) {
    keys.add("lead_firstname");
    keys.add("lead_lastname");
  }
  rows.push(row);
};

const fetchContactPages = async ({ userId, extraConstraints, rows, keys, onProgress, includeRow }) => {
  const baseRef = collection(firestore, `World/${userId}/db`);
  let lastDoc = null;
  let scanned = 0;

  while (true) {
    const constraints = [...extraConstraints, orderBy("__name__")];
    if (lastDoc) constraints.push(startAfter(lastDoc));
    constraints.push(limit(PAGE_SIZE));

    const snapshot = await getDocs(query(baseRef, ...constraints));
    if (snapshot.empty) break;

    snapshot.docs.forEach((d) => appendContactDoc(d, rows, keys, includeRow));
    lastDoc = snapshot.docs[snapshot.docs.length - 1];
    scanned += snapshot.docs.length;
    if (onProgress) onProgress(scanned);
    if (snapshot.docs.length < PAGE_SIZE) break;
  }
};

export const fetchContacts = async ({
  userId,
  filterLeadsOnly = false,
  importNames = null,
  extraKeys = [],
  onProgress,
} = {}) => {
  if (!userId) return { rows: [], keys: ["id"] };

  const rows = [];
  const keys = new Set(["id", ...uniqueStrings(extraKeys)]);
  const listNames = normalizeImportNames(importNames);
  const customKeys = await loadCustomFieldKeys(userId);
  customKeys.forEach((key) => keys.add(key));

  const extraConstraints = filterLeadsOnly ? [where("lead_status", "==", "Lead")] : [];
  // List filter stays in memory: `import_name == X` + `orderBy(__name__)` has no
  // composite index, and `orderBy(timestamp)` would drop contacts without that field.
  const wanted = listNames.length > 0 ? new Set(listNames) : null;

  await fetchContactPages({
    userId,
    extraConstraints,
    rows,
    keys,
    onProgress,
    includeRow: wanted ? (row) => wanted.has(row.import_name) : null,
  });

  return { rows, keys: Array.from(keys) };
};
